import { ComponentType } from 'react';

import { BillToAddressCard, CompanyCard, SoldToAddressCard } from '../../../../../components/cards/company';
import { FinancialAttachmentCard, PayerAddressCard } from '../../../../../components/cards/financial';
import { AdditionalAttachmentCard } from '../../../../../components/cards/financial/AttachmentCard';
import PrivacyPolicyCard from '../../../../../components/cards/financial/PrivacyPolicyCard';
import {
  CertOfAnalysisContactCard,
  DeliveryDetailsCard,
  DeliveryInstructionsCard,
  InvoiceDeliveryContactCard,
  OrderConfirmationContactCard,
  PackingListContactCard,
  ShipToAddressCard
} from '../../../../../components/cards/shipTo';
import CompanyPage from '../../../../../features/CompanyPage';
import FinancialPage from '../../../../../features/FinancialPage';
import ReviewPage from '../../../../../features/ReviewPage';
import ShipToPage from '../../../../../features/ShipTo';
import { CARD_CATEGORIES } from '../../../../../shared/cardConfig/constants';
import {
  // EDI_FIELDS,
  ATTACHMENT_FIELDS,
  BILL_TO_ADDRESS_FIELDS,
  COMPANY_FIELDS,
  CONTACT_FIELDS,
  CONTACT_SAME_AS_FIELDS,
  DELIVERY_DETAILS_FIELDS,
  DELIVERY_INSTRUCTIONS_FIELDS,
  PAYER_ADDRESS_FIELDS,
  SHIP_TO_ADDRESS_FIELDS,
  SOLD_TO_ADDRESS_FIELDS
} from '../../../../../shared/fieldConfig/constants';
import { PAGE_ROUTES } from '../../../../../utils/constants';
import { getShipToSubtitle } from '../../../../../utils/hooksLib';
import billToPages from './billTo';
import payerPages from './payer';
import shipToPages from './shipTo';
import soldToPages from './soldTo';

// ----------------- Types -----------------
export interface Field {
  key: string;
  required?: boolean;
}

export interface Card {
  key: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>;
  fields?: Field[];
  fieldWrapper?: string;
  required?: boolean;
  sameAsFields?: string[];
}

export interface Accordion {
  title: string;
  cards?: Card[];
  count?: number;
  subtitle?: string;
}

export interface Page {
  route: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>;
  cards?: Card[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  accordions?: Array<Accordion | ((args: any) => any)>;
  validateFinancials?: boolean;
}

// ******* PAGE AND CARD CONFIGS **********

const companyPageTemplate = {
  route: PAGE_ROUTES.company,
  Component: CompanyPage,
  cards: [
    {
      key: CARD_CATEGORIES.company,
      Component: CompanyCard,
      fields: [{ key: COMPANY_FIELDS.legalName }, { key: COMPANY_FIELDS.tradeDbaName }]
    },
    {
      key: CARD_CATEGORIES.soldTo,
      Component: SoldToAddressCard,
      fields: [
        { key: SOLD_TO_ADDRESS_FIELDS.address },
        { key: SOLD_TO_ADDRESS_FIELDS.taxNumber1 },
        { key: SOLD_TO_ADDRESS_FIELDS.taxExempt }
      ]
    },
    // For EDI card (or)customer integration card use below PR to get the code.
    // https://git.cglcloud.com/CustomerExperience/cgl-dxo-redcarpet-fe-onboarding/commit/0769773ac3499a5aa52c2ea38012e068d061245b
    // https://git.cglcloud.com/CustomerExperience/cgl-dxo-redcarpet-fe-onboarding/commit/fb05d487a4bec5e06c29f5ab50c309947e051b23 (use this PR for EDI card changes)
    // https://git.cglcloud.com/CustomerExperience/cgl-dxo-redcarpet-fe-onboarding/pull/709/commits/6c56b91a6ccd86e1733345f52c593a77932807b3 (use this PR for EDI card changes)
    {
      key: CARD_CATEGORIES.billTo,
      Component: BillToAddressCard,
      fields: [{ key: BILL_TO_ADDRESS_FIELDS.address }]
    }
  ]
};

const contactFields = [{ key: CONTACT_FIELDS.email }, { key: CONTACT_FIELDS.fax }];

const shipToPageTemplate = {
  route: PAGE_ROUTES.shipTo,
  Component: ShipToPage,
  cards: [
    {
      key: CARD_CATEGORIES.address,
      Component: ShipToAddressCard,
      fields: [{ key: SHIP_TO_ADDRESS_FIELDS.address }, { key: SHIP_TO_ADDRESS_FIELDS.taxExempt }],
      fieldWrapper: CARD_CATEGORIES.shipTo
    },
    {
      key: CARD_CATEGORIES.deliveryDetails,
      Component: DeliveryDetailsCard,
      fields: [
        { key: DELIVERY_DETAILS_FIELDS.appointmentRequired },
        { key: DELIVERY_DETAILS_FIELDS.receivingPlantContactName },
        { key: DELIVERY_DETAILS_FIELDS.receivingPlantPhoneNumber },
        { key: DELIVERY_DETAILS_FIELDS.afterHoursContactName },
        { key: DELIVERY_DETAILS_FIELDS.afterHoursPhoneNumber },
        { key: DELIVERY_DETAILS_FIELDS.warehouseReceivingHours }
      ],
      fieldWrapper: CARD_CATEGORIES.shipTo
    },
    {
      key: CARD_CATEGORIES.deliveryInstructions,
      Component: DeliveryInstructionsCard,
      fields: [
        { key: DELIVERY_INSTRUCTIONS_FIELDS.shippingInstructions },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.customsText },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.loadingInstructions },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.unloadingInstructions },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.packingInstructions },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.billOfLadingText }
      ],
      fieldWrapper: CARD_CATEGORIES.shipTo
    },
    {
      key: CARD_CATEGORIES.invoiceDelivery,
      Component: InvoiceDeliveryContactCard,
      fields: [...contactFields],
      fieldWrapper: CARD_CATEGORIES.shipTo
    },
    {
      key: CARD_CATEGORIES.coa,
      Component: CertOfAnalysisContactCard,
      fields: contactFields,
      fieldWrapper: CARD_CATEGORIES.shipTo,
      required: true,
      sameAsFields: [CONTACT_SAME_AS_FIELDS.sameAsInvoiceDelivery]
    },
    {
      key: CARD_CATEGORIES.packingList,
      Component: PackingListContactCard,
      fields: contactFields,
      required: true,
      fieldWrapper: CARD_CATEGORIES.shipTo,
      sameAsFields: [CONTACT_SAME_AS_FIELDS.sameAsInvoiceDelivery, CONTACT_SAME_AS_FIELDS.sameAsCoa]
    },
    {
      key: CARD_CATEGORIES.orderConfirmation,
      Component: OrderConfirmationContactCard,
      fields: contactFields,
      fieldWrapper: CARD_CATEGORIES.shipTo,
      required: true,
      sameAsFields: [
        CONTACT_SAME_AS_FIELDS.sameAsInvoiceDelivery,
        CONTACT_SAME_AS_FIELDS.sameAsCoa,
        CONTACT_SAME_AS_FIELDS.sameAsPackingList
      ]
    }
  ]
};

const financialPageTemplate = {
  route: PAGE_ROUTES.financial,
  Component: FinancialPage,
  validateFinancials: true,
  cards: [
    {
      key: CARD_CATEGORIES.payer,
      Component: PayerAddressCard,
      fields: [
        { key: PAYER_ADDRESS_FIELDS.address },
        { key: PAYER_ADDRESS_FIELDS.taxNumber1, required: true },
        { key: PAYER_ADDRESS_FIELDS.dnbNumber }
      ]
    },
    // {
    //   key: CARD_CATEGORIES.bankReference,
    //   Component: BankReferenceCard,
    //   fields: [
    //     { key: BANK_REFERENCE_FIELDS.name, required: true },
    //     // { key: BANK_REFERENCE_FIELDS.routingNumber },
    //     { key: BANK_REFERENCE_FIELDS.accountNumber },
    //     { key: BANK_REFERENCE_FIELDS.address },
    //     { key: BANK_REFERENCE_FIELDS.faxNumber },
    //   ],
    // },
    // {
    //   key: CARD_CATEGORIES.bankerContact,
    //   Component: BankerContactCard,
    //   fields: [
    //     { key: BANKER_CONTACT_FIELDS.contactName },
    //     { key: BANKER_CONTACT_FIELDS.email },
    //     { key: BANKER_CONTACT_FIELDS.phoneNumber },
    //   ],
    // },
    {
      key: CARD_CATEGORIES.financialAttachmentCard,
      Component: FinancialAttachmentCard,
      fields: [{ key: ATTACHMENT_FIELDS.financialCardAttachment }]
    },
    {
      key: CARD_CATEGORIES.additionalAttachmentCard,
      Component: AdditionalAttachmentCard,
      fields: [{ key: ATTACHMENT_FIELDS.additionalAttachment }]
    },
    {
      key: CARD_CATEGORIES.privacyPolicy,
      Component: PrivacyPolicyCard
    }
  ]
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getShipToCardsForReviewPage({ store }: { store: any }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return store.customer.shipTo.map((shipTo: any, i: number) => ({
    title: `tabTitles.ship to`,
    count: i + 1,
    subtitle: getShipToSubtitle({ shipToAddress: shipTo.address, store }),
    cards:
      i === 0
        ? shipToPageTemplate.cards
        : shipToPageTemplate.cards.filter(({ key }) => CARD_CATEGORIES.invoiceDelivery !== key)
  }));
}

const reviewPageTemplate = {
  route: PAGE_ROUTES.review,
  Component: ReviewPage,
  accordions: [
    {
      title: 'tabTitles.company',
      cards: companyPageTemplate.cards
    },
    getShipToCardsForReviewPage,
    {
      title: 'tabTitles.financial',
      cards: [...financialPageTemplate.cards]
    }
  ]
};

// *********** TEMPLATE ************

export const templateMap = {
  customer_set_up: {
    pages: [companyPageTemplate, shipToPageTemplate, financialPageTemplate, reviewPageTemplate]
  },
  bill_to: {
    pages: billToPages
  },
  payer: {
    pages: payerPages
  },
  ship_to: {
    pages: shipToPages
  },
  sold_to: {
    pages: soldToPages
  }
};
